import './components/config-permission';
import './components/cti-auth';
import './components/agent-assist-login';
import './components/request-close';
import './components/spinner';
import '@a365/design-system';

import { HostName, app, pages } from '@microsoft/teams-js';
import { IWebAgentDependencies, WebAgentLogin } from '@a365/webagent-login';

import { AppConfigService } from './services/app.config.service';
import { AppInsightsLogService } from './services/appinsights.service';
import { CommunicationPlatform } from './dependencies/dependencies';
import { PluginService } from './services/plugin.service';
import { RoutingService } from '@a365/routing';
import { Translations } from '@a365/translations';
import { WebAgentService } from './services/webagent.service';
import { environment } from './environments/environment';
import { loadAppConfig } from './app.config';

const latest = 'latest';
let faviconHref;

class Version {
    private major: number | typeof latest;
    private minor?: number;
    private patch?: number;

    constructor(versionPath: string) {
        if (versionPath === latest) {
            this.major = latest;
        } else {
            [ this.major, this.minor, this.patch ] = versionPath.split('.').map(v => Number.parseInt(v));
        }
    }

    isBefore(major: number, minor: number, patch = 0) {
        if (this.major === latest) {
            return false;
        }
        if (major > this.major) {
            return true;
        }
        if (major === this.major) {
            return minor > this.minor || (minor === this.minor && patch > this.patch);
        }
        return false;
    }

    toString() {
        if (this.major === latest) {
            return latest;
        }
        return `v${this.major}.${this.minor}.${this.patch}`;
    }
}

declare let WebAgent: (params: any) => Promise<void>;
export let webAgentService: WebAgentService;
export let pluginService: PluginService;
export let appConfigService: AppConfigService;
export let translationService: Translations;

const initTranslations = async (location: string, fallbackLocation: string) => {
    const translations = new Translations(location, fallbackLocation);
    await translations.whenInitialized;
    return translations;
};

const addScript = (url) => {
    return new Promise<Event>((resolve => {
        const scriptTag = document.createElement('script');
        scriptTag.src = url;
        scriptTag.defer = false;
        scriptTag.async = false;
        scriptTag.onload = resolve;
        document.head.appendChild(scriptTag);
    }));
};

const getWebAgent = async (version: Version) => {
    const subPath = version.isBefore(6, 33)
        ? 'evergreen/'
        : '';

    const scripts = [
        `dist-webagent/${version}/WebAttendant/${subPath}vendors~webagent.js`,
        `dist-webagent/${version}/WebAttendant/${subPath}framework~webagent.js`,
        `dist-webagent/${version}/WebAttendant/${subPath}components~webagent.js`,
        `dist-webagent/${version}/WebAttendant/${subPath}webagent.js`
    ];

    await Promise.all(scripts.map(url => addScript(url)));
};

const WebAgentBootstrap = (function() {
    async function initializeApp() {
        if ('serviceWorker' in navigator) {
            try {
                await navigator.serviceWorker.register('service-worker.js');
                await navigator.serviceWorker.ready;
            } catch (e) {
                console.log('Service worker registration failed:', e);
            }
        } else {
            console.info('This browser doesn\'t support web workers');
        }
        let permission = 'Notification' in window ? Notification.permission : 'denied';
        const appConfig = await loadAppConfig(environment.config);
        translationService = await initTranslations(appConfig.translationsUrl, 'assets/i18n/en.json');
        const webagentLogin = new WebAgentLogin(appConfig, translationService);
        webAgentService = new WebAgentService();
        pluginService = new PluginService();
        appConfigService = new AppConfigService(appConfig);

        document.querySelector('html').lang = translationService.language ?? 'en';

        const pathHubs = window.location.pathname.split('/').filter(p => p.length);
        const appName = pathHubs.length > 1 ? pathHubs[0] : '';
        const currentHref = Array.from(document.querySelectorAll('link'))
            .find(link => /\/favicon\.ico$/.test(link.href)).href;
        const rootRoute = currentHref.substring(0, currentHref.lastIndexOf('/'));
        if (rootRoute === window.location.origin) {
            RoutingService.rootRoute = '';
        } else {
            const relativeRoute = rootRoute.startsWith(window.location.origin)
                ? rootRoute.substring(window.location.origin.length + 1)
                : rootRoute;
            RoutingService.rootRoute = relativeRoute;
        }

        const routing = new RoutingService('login');
        const rootElement = document.body;
        routing.setRootElement(rootElement);

        routing.registerRoute([ 'login', 'redirect' ], '<wsp-login id="wsp-login"></wsp-login>');

        routing.registerRoute(
            'notification-permission',
            (parent: HTMLElement) => {
                const component = document.createElement('config-permission-component');
                component.setAttribute('header', 'ACCEPTNOTIFICATIONS.HEADER');
                component.setAttribute('body', 'ACCEPTNOTIFICATIONS.TEXT');
                parent.innerHTML = '';
                parent.appendChild(component);

                Notification.requestPermission().then(result => {
                    permission = result;
                    window.close();
                });
            }
        );

        const teamsInitPromise = app.initialize();

        routing.registerRoute(
            'msteams-config',
            async (parent: HTMLElement) => {
                const component = document.createElement('config-permission-component');
                component.setAttribute('header', 'TEAMSCONFIG.HEADER');
                component.setAttribute('body', 'TEAMSCONFIG.TEXT');
                parent.innerHTML = '';
                parent.appendChild(component);

                let url = window.location.origin + (appName.length ? '/' + appName : '');
                if (!url.endsWith('/')) {
                    url = `${url}/`;
                }

                try {
                    await teamsInitPromise;
                    pages.config.setValidityState(true);
                    pages.config.registerOnSaveHandler(async saveEvent => {
                        try {
                            await pages.config.setConfig({
                                websiteUrl: url,
                                contentUrl: url,
                                entityId: 'WebAgent',
                                suggestedDisplayName: 'WebAgent'
                            });
                            saveEvent.notifySuccess();
                        } catch (error) {
                            saveEvent.notifyFailure(error);
                        }
                    });
                } catch {
                    // In case this page is opened outside of the Teams Client context, we do nothing
                }
            }
        );

        routing.registerRoute(
            'cti-auth',
            (parent: HTMLElement) => {
                const component = document.createElement('cti-auth-component');
                parent.innerHTML = '';
                parent.appendChild(component);
            }
        );

        routing.registerRoute(
            'agentassistlogin',
            (parent: HTMLElement) => {
                const component = document.createElement('agent-assist-login-component');
                parent.innerHTML = '';
                parent.appendChild(component);
            }
        );
        routing.registerRoute(
            'requestclose',
            (parent: HTMLElement) => {
                const component = document.createElement('request-close-component');
                parent.innerHTML = '';
                parent.appendChild(component);
            }
        );

        let isTeams;
        try {
            await teamsInitPromise;

            const context = await app.getContext();
            isTeams = context
                ? context.app.host.name === HostName.teams || context.app.host.name === HostName.teamsModern
                : false;
        } catch {
            isTeams = false;
        }

        // for now do not ask permission when in teams to not be rejected for approval because we open a tab
        if (!isTeams && window.location !== window.parent.location && permission !== 'granted') {
            window.open('./notification-permission');
        }

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('load-plugin')) {
            const loadPluginPath = urlParams.get('load-plugin');
            const regex = /^[a-z0-9/_.-]+\.js$/i;
            const correctFormat = regex.test(loadPluginPath);
            if (!correctFormat) {
                console.warn(
                    `Found load-plugin url parameter with value ${loadPluginPath}.\n`
                    + 'The load-plugin parameter is not formatted correctly (load-plugin=<RelativePluginPath><ScriptFileName>.js,\n'
                    + 'where <RelativePluginPath> is a path to a script file relative to the WebAgent).\n'
                    + 'Example: <webagent-url>?load-plugin=assets/plugins/load-dynamics-cif-v2.js'
                );
            } else {
                await pluginService.forceLoadPlugin(loadPluginPath);
            }
        }

        webagentLogin.onLoggedIn(async (webAgentDependencies: IWebAgentDependencies) => {
            const urlParams = new URLSearchParams(window.location.search);
            const forceVersion = urlParams.get('force-version');
            const bundleVersion = await webAgentService.fetchWebAgentVersion(
                webAgentDependencies.webAgentServerUrl,
                webAgentDependencies.token
            );
            const versionString = forceVersion ?? bundleVersion ?? '6.26.0';
            const version = new Version(versionString);

            if (version.isBefore(6, 34)) {
                document.body.classList.add('legacy');
            }

            const faviconLink = Array.from(document.querySelectorAll('link'))
                .find(link => link.href.endsWith('favicon.ico'));
            faviconHref ??= faviconLink.href; // save original href for when the user logs out and tries logging in again
            faviconLink.href = faviconHref.replace(/favicon.ico$/, `dist-webagent/${version}/WebAttendant/favicon.ico`);

            const logInData = {
                baseUrl: window.location.origin + window.location.pathname,
                browserLanguage: window.navigator.language.substring(0, 2),
                bundleVersion: bundleVersion ?? '-',
                forceVersionUsed: urlParams.has('force-version'),
                fullUrl: window.location.href,
                graphqlUrl: webAgentDependencies.graphqlUrl,
                loadedLanguage: translationService.language,
                platform: webAgentDependencies.platform,
                referrer: webAgentDependencies.referrer,
                signinAddress: webAgentDependencies.signinAddress,
                tokenType: webAgentDependencies.accessTokenType,
                upn: webAgentDependencies.upn,
                webAgentServerUrl: webAgentDependencies.webAgentServerUrl,
                webAgentVersion: versionString
            };

            const appInsightsLogService = new AppInsightsLogService();
            appInsightsLogService.initialize(appConfig.appInsightsConfig);
            appInsightsLogService.setAuthenticatedUserContext(webAgentDependencies.signinAddress);
            appInsightsLogService.trackInfo('Login data', logInData);

            // Un-initialize AppInsights before loading WebAgent to prevent duplicate logging
            appInsightsLogService.unload();

            await getWebAgent(version);

            // restore some properties and methods for backwards compatibility
            if (version.isBefore(6, 27)) {
                appConfig['msalConfig'] = {
                    tenant: 'common',
                    clientId: appConfig.oidcConfig.clientId,
                    instance: 'https://login.microsoftonline.com/',
                    endpoints: {
                        'https://webdir.online.lync.com': 'https://webdir.online.lync.com',
                        'https://graph.microsoft.com': 'https://graph.microsoft.com'
                    }
                };
                if (webAgentDependencies.platform as any as CommunicationPlatform === CommunicationPlatform.Teams) {
                    const { GraphOAuthService }: any = await import(/* webpackChunkName: "legacy.graph.oauth" */ './legacy/graph.oauth.service');
                    webAgentDependencies.authenticationService['teamsService'] =
                        new GraphOAuthService(webAgentDependencies.authenticationService);
                }
                webAgentDependencies.authenticationService['checkPermissions'] = async (scopes) => {
                    const hasPermissions = webAgentDependencies.authenticationService.hasPermissions
                        .call(webAgentDependencies.authenticationService, scopes);
                    if (hasPermissions) {
                        return await webAgentDependencies.authenticationService.getToken();
                    }
                };

                const account = await webAgentDependencies.authenticationService.getAccount();
                webAgentDependencies.authenticationService['account'] = account;
            }

            await translationService.fetchFallbackTranslations(`dist-webagent/${version}/WebAttendant/assets/i18n/en.json`);

            await WebAgent({
                appConfig,
                authenticationService: webAgentDependencies.authenticationService,
                fetchToken: webAgentDependencies.fetchToken,
                onTokenRefreshed: webagentLogin.onTokenRefreshed.bind(webagentLogin),
                permission,
                platform: webAgentDependencies.platform,
                referrer: webAgentDependencies.referrer,
                relativePath: `dist-webagent/${version}/WebAttendant`,
                routing,
                signinAddress: webAgentDependencies.signinAddress,
                token: webAgentDependencies.token,
                translations: translationService,
                version: versionString,
                webAgentServerUrl: webAgentDependencies.webAgentServerUrl,
                graphqlUrl: webAgentDependencies.graphqlUrl
            });
        });

        routing.bindEvents();
    }
    return {
        initializeApp
    };
})();

window['WebAgentBootstrap'] = WebAgentBootstrap;
